import "./Notify.scss";
import * as React from "react";
import * as PropTypes from "prop-types";
import { autobind } from "office-ui-fabric-react/lib/Utilities";
import {
  ClientId,
  ISharingLinkSettings,
  ISharingInformation,
  ISharingStore,
  IEngagementExtraData,
  ShareType,
  ICustomLabeledAudienceConfig,
  Mode
} from "../../interfaces/SharingInterfaces";
import { ShareNotification } from "../ShareNotification/ShareNotification";
import { Header } from "../Header/Header";
import { ActivityIndicator } from "../ActivityIndicator/ActivityIndicator";
import { IPerson } from "@ms/odsp-datasources/lib/PeoplePicker";
import { IconButton } from "office-ui-fabric-react/lib/Button";
import stringResx from "../Share.resx";
import PeoplePicker from "../PeoplePicker/PeoplePicker";
import * as PeoplePickerHelper from "../../utilities/PeoplePickerHelper";
import PrincipalType from "@ms/odsp-datasources/lib/dataSources/roleAssignments/PrincipalType";
import * as StringHelper from "@ms/odsp-utilities/lib/string/StringHelper";
import { css } from "office-ui-fabric-react/lib/Utilities";
import { Label } from "office-ui-fabric-react/lib/Label";
import { Link } from "office-ui-fabric-react/lib/Link";
import {
  INotifyGroupRecipient,
  NotifyGroupRecipient
} from "./NotifyGroupRecipient";
import {
  INotifyPersonRecipient,
  NotifyPersonRecipient
} from "./NotifyPersonRecipient";
import SendButton from "../SendButton/SendButton";
import * as ShareHelper from "../../utilities/ShareHelper";
import SendButtonType from "../SendButton/SendButtonType";
import { SharingLinkKind, AccessStatus, ShareViewState } from "../../Share";
import * as ShareCommunicationManager from "../../utilities/ShareCommunicationManager";
import { Checkbox } from 'office-ui-fabric-react/lib/Checkbox';
import { MessageBar, MessageBarType } from 'office-ui-fabric-react/lib/MessageBar';

const RECIPIENT_LIMIT = 100;
const LARGE_RECIPIENT_WARNING = 20;

export interface INotifyProps {
  clientId: ClientId;
  scenarioId?: string;
  currentSettings: ISharingLinkSettings;
  sharingInformation: ISharingInformation;
  engagementExtraData: IEngagementExtraData;
  customLabeledAudienceConfig: ICustomLabeledAudienceConfig;
}

export interface INotifyState {
  isExpanded: boolean;
  groupsIsExpanded: boolean;
  count: number;
  people: INotifyPersonRecipient[];
  groups: INotifyGroupRecipient[];
  peoplePickerRecipients: IPerson[];
  peoplePickerError: JSX.Element;
  showActivityIndicator: boolean;
  permissionsMap: { [index: string]: AccessStatus };
  viewState: ShareViewState;
}

export class Notify extends React.Component<INotifyProps, INotifyState> {
  private _sharingStore: ISharingStore;
  private _resize: () => void;
  private _shareClickTime: number;

  static contextTypes = {
    resize: PropTypes.func.isRequired,
    strings: PropTypes.object.isRequired,
    sharingStore: PropTypes.object.isRequired,
    shareClickTime: PropTypes.number
  };

  constructor(props: INotifyProps, context: any) {
    super(props);
    this._sharingStore = context.sharingStore;
    this._shareClickTime = context.shareClickTime;
    this._resize = context.resize;

    this.state = {
      isExpanded: false,
      groupsIsExpanded: false,
      count: 0,
      peoplePickerRecipients: [],
      groups: [],
      people: [],
      peoplePickerError: null,
      showActivityIndicator: false,
      permissionsMap: {},
      viewState: ShareViewState.notify
    };
  }

  public componentWillMount() {
    const props = this.props;
    this._sharingStore.addListener(this._onStoreUpdate);

    let sharingPrincipals = props.sharingInformation.sharingPrincipals;
    let sharingLinkPrincipals = props.sharingInformation.sharingLinks.reduce(
      (a, b) => a.concat(b.principals),
      []
    );
    let recipients: IPerson[] = sharingLinkPrincipals
      .concat(sharingPrincipals)
      .map(recipient => {
        return {
          userId: recipient.loginName,
          name: recipient.primaryText,
          job: recipient.secondaryText,
          email: recipient.email !== "" ? recipient.email : recipient.loginName, // SP security groups do not have emails.
          principalType: recipient.type,
          image: recipient.imageUrl,
          rawPersonData: {
            Key: recipient.loginName,
            EntityData: {
              SPGroupID: recipient.id
            }
          }
        };
      });

    let groups: INotifyGroupRecipient[] = recipients
      .filter(r => r.principalType !== PrincipalType.user)
      .map(r => {
        return {
          ...r,
          checked: false,
          expanded: false,
          disabled: true,
          groupId: r.rawPersonData.groupId,
          memberCount: null
        };
      });
    groups.forEach(group => {
      this._sharingStore.fetchMemberCount(group);
    });

    let people: INotifyPersonRecipient[] = recipients.filter(
      r => r.principalType === PrincipalType.user
    );
    let permissionsMap: { [index: string]: AccessStatus } = {};
    people.concat(groups).forEach(x => {
      permissionsMap[x.email] = AccessStatus.hasAccess;
    });

    let peoplePickerGroups: IPerson[] = this.state.peoplePickerRecipients.filter(r => r.principalType !== PrincipalType.user);
    peoplePickerGroups.forEach(peoplePickerGroup => {
      this._sharingStore.fetchMemberCount(peoplePickerGroup);
    })

    this.setState({
      isExpanded: true,
      groupsIsExpanded: true,
      count: 0,
      peoplePickerRecipients: [],
      groups: groups,
      people: people,
      peoplePickerError: null,
      permissionsMap: permissionsMap,
      viewState: ShareViewState.notify
    });
  }

  public componentDidMount() {
    ShareCommunicationManager.pageFinishedLoading(
      this._shareClickTime,
      this.props.clientId,
      Mode.notify,
      this.props.scenarioId
    );
    this.setState({ groupsIsExpanded: false, isExpanded: false });
  }

  public componentDidUpdate(prevProps: INotifyProps, prevState: INotifyState) {
    this._resize();
  }

  @autobind
  private _onExpandClick() {
    this.setState(
      (previousState: INotifyState) => {
        return {
          ...previousState,
          isExpanded: !previousState.isExpanded
        };
      },
      () => {
        this._sharingStore.getEngagementHelper().log(
          "Notify.ExpandLinkClicked",
          this.props.engagementExtraData
        );
      }
    );
  }

  @autobind
  private _renderPeoplePicker(): JSX.Element {
    const state = this.state;
    const props = this.props;

    return (
      <div className={ css("od-Notify-PeoplePicker") }>
        <PeoplePicker
          defaultSelectedItems={ state.peoplePickerRecipients }
          onChange={ this._onPeoplePickerSelected }
          onResolvedSuggestions={ this._onResolvedSuggestions }
          pickerSettings={ props.sharingInformation.peoplePickerSettings }
          sharingLinkKind={ props.currentSettings.sharingLinkKind }
          error={ state.peoplePickerError }
          sharingInformation={ props.sharingInformation }
          permissionsMap={ state.permissionsMap }
          clientId={ props.clientId }
        />
      </div>
    );
  }

  @autobind
  private _onStoreUpdate() {
    let newPermissionsMap = this._sharingStore.getPermissionsMap();
    let peoplePickerError = PeoplePickerHelper.renderPickerError({
      selectedItems: this.state.peoplePickerRecipients,
      sharingLinkKind: SharingLinkKind.direct,
      canAddExternalPrincipal: this.props.sharingInformation
        .canAddExternalPrincipal,
      sharingInformation: this.props.sharingInformation,
      viewPolicyTipCallback: this._sharingStore.getPolicyTipInformation,
      permissionsMap: newPermissionsMap
    });

    if (peoplePickerError) {
      this._sharingStore.getEngagementHelper().log(
        "Notify.AddedUserWhoDoesNotHaveAccess",
        this.props.engagementExtraData
      );
    }

    newPermissionsMap = { ...this.state.permissionsMap, ...newPermissionsMap };

    this.setState((previousState: INotifyState) => ({
      ...previousState,
      peoplePickerError: peoplePickerError,
      permissionsMap: newPermissionsMap
    }));
  }

  @autobind
  private _onPeoplePickerSelected(recipients: Array<IPerson>) {
    this._sharingStore.checkPermissions(recipients);
    recipients.filter(r => r.principalType !== PrincipalType.user).forEach(group => {
      this._sharingStore.fetchMemberCount(group);
    })

    this._sharingStore.getEngagementHelper().log(
      "Notify.AddedUserFromPeoplePicker",
      this.props.engagementExtraData
    );

    let newPermissionsMap = this._sharingStore.getPermissionsMap();
    let peoplePickerError = PeoplePickerHelper.renderPickerError({
      selectedItems: this.state.peoplePickerRecipients,
      sharingLinkKind: SharingLinkKind.direct,
      canAddExternalPrincipal: this.props.sharingInformation
        .canAddExternalPrincipal,
      sharingInformation: this.props.sharingInformation,
      viewPolicyTipCallback: this._sharingStore.getPolicyTipInformation,
      permissionsMap: newPermissionsMap
    });
    newPermissionsMap = { ...this.state.permissionsMap, ...newPermissionsMap };

    this.setState((previousState: INotifyState) => ({
      ...previousState,
      peoplePickerRecipients: recipients,
      peoplePickerError: peoplePickerError,
      permissionsMap: newPermissionsMap
    }), () => {
      this._sharingStore.getEngagementHelper().peoplePickerLinkedinSuggestionSelected(this.props.engagementExtraData, recipients);
    });
  }

  @autobind
  private _onResolvedSuggestions(suggestions: IPerson[]): IPerson[] {
    this._sharingStore.getEngagementHelper().peoplePickerLinkedinSuggestions(this.props.engagementExtraData, suggestions);
    return suggestions;
  }

  @autobind
  private _updateGroupCount(): void {
    let newMemberCount = this._sharingStore.getMembersCounts();
    let newGroups = this.state.groups.slice(0);

    for (let index = 0; index < newGroups.length; index++) {
      let group = this.state.groups[index];
      if (newMemberCount[group.email] !== group.memberCount) {
        group.memberCount = newMemberCount[group.email];
        group.checked = false;
        group.disabled = group.memberCount >= RECIPIENT_LIMIT;
        newGroups[index] = group;
      }
    }
    this.setState((previousState: INotifyState) => ({
      ...previousState,
      groups: newGroups
    }));
  }

  @autobind
  private _computeTotalGroupsMembersCount() {
    const state = this.state;
    let membersCounts = this._sharingStore.getMembersCounts();
    let count: number = state.groups
      .filter(group => group.checked && membersCounts[group.email])
      .map(group => membersCounts[group.email])
      .reduce((accumulator, currentValue) => accumulator + currentValue, 0);
    return count;
  }

  @autobind
  private _computeTotalMemberCount() {
    const memberCounts = this._sharingStore.getMembersCounts();
    let uniqueRecipients: { [index: string]: IPerson } = {};
    this._allRecipients().forEach(r => {
      if (!uniqueRecipients[r.email]) {
        uniqueRecipients[r.email] = r;
      }
    });

    const count: number = Object.keys(uniqueRecipients)
      .map((email) => {
        if (uniqueRecipients[email].principalType === PrincipalType.user) {
          return 1;
        } else {
          return memberCounts[email] || 0;
        }
      })
      .reduce((accumulator, currentValue) => accumulator + currentValue, 0);
    return count;
  }

  @autobind
  private _updateCheckboxForGroup(group: INotifyGroupRecipient) {
    let newGroups = this.state.groups;
    newGroups.map(newGroup => {
      if (newGroup.email === group.email) {
        return group;
      } else {
        return newGroup;
      }
    });
    this.setState({ groups: newGroups });
  }

  @autobind
  private _updateCheckboxForPerson(person: INotifyPersonRecipient) {
    let newPeople = this.state.people;
    newPeople.map(newPerson => {
      if (newPerson.email === person.email) {
        return person;
      } else {
        return newPerson;
      }
    });
    this.setState({ people: newPeople });
  }

  @autobind
  private _allRecipients() {
    const state = this.state;
    let recipients: IPerson[] = state.peoplePickerRecipients
      .concat(state.people.filter(p => p.checked))
      .concat(state.groups.filter(g => g.checked));
    return recipients;
  }

  @autobind
  private _showSuccessMessage() {
    this.setState((previousState: INotifyState) => ({
      ...previousState,
      viewState: ShareViewState.linkSuccess,
      showActivityIndicator: false
    }));
  }

  @autobind
  private _sendNotification() {
    let recipients: IPerson[] = this._allRecipients();

    this.setState(
      {
        showActivityIndicator: true
      },
      () => {
        ShareCommunicationManager.sendLinkClicked();
        this._sharingStore.shareLink({
          settings: this.props.currentSettings,
          recipients: recipients,
          allRecipientsHaveAccess: true
        });
        this._sharingStore.getEngagementHelper().shareCompleted({
          ...this.props.engagementExtraData,
          recipientsCount: recipients.length
        });
        ShareCommunicationManager.shareCompleted(ShareType.share);
        this._showSuccessMessage();
      }
    );
  }

  @autobind
  private _renderGroup(group: INotifyGroupRecipient): JSX.Element {
    return (
      <NotifyGroupRecipient
        clientId={ this.props.clientId }
        group={ group }
        updateCountCallback={ this._updateGroupCount }
        updateCheckboxForGroup={ this._updateCheckboxForGroup }
        engagementExtraData={ this.props.engagementExtraData }
      />
    );
  }

  @autobind
  private _onGroupsExpandClick() {
    const groupsIsExpanded = this.state.groupsIsExpanded;
    this.setState((previousState: INotifyState) => ({
      ...previousState,
      groupsIsExpanded: !groupsIsExpanded
    }), () => {
      this._sharingStore.getEngagementHelper().log(
        "Notify.GroupsExpandClicked",
        this.props.engagementExtraData
      );
    });
  }

  @autobind
  private _renderGroups(groups: INotifyGroupRecipient[]): JSX.Element {
    const state = this.state;
    const groupIsExpanded = state.groupsIsExpanded ? " expanded " : "";
    const groupIsScrollable = state.groups.length > 5 ? " scroll " : "";
    if (groups.length === 0) {
      return;
    }

    return (
      <div className={ css("od-Notify-groups-container") }>
        <div className={ css("od-Notify-groups-expand") }>
          <Label className={ css("od-Notify-groupLabel") }>
            { stringResx.notifyGroupsLabel }
          </Label>
          <IconButton
            className={ "od-Notify-group-expand-icon" }
            iconProps={ {
              iconName: groupIsExpanded ? "ChevronUp" : "ChevronDown"
            } }
            onClick={ this._onGroupsExpandClick }
          />
        </div>
        { this._displayTotalGroupsMembersCount() }
        <div
          className={ css(
            "od-Notify-groups-recipient-container" +
            groupIsExpanded +
            groupIsScrollable
          ) }
        >
          { groupIsExpanded &&
            groups.map(group => {
              return (
                <div
                  key={ group.email }
                  className={ css("od-Notify-groups-listItem") }
                >
                  { this._renderGroup(group) }
                </div>
              );
            }) }
        </div>
      </div>
    );
  }

  @autobind
  private _renderPerson(person: INotifyPersonRecipient): JSX.Element {
    return (
      <NotifyPersonRecipient
        clientId={ this.props.clientId }
        person={ person }
        updateCheckboxForPerson={ this._updateCheckboxForPerson }
        engagementExtraData={ this.props.engagementExtraData }
      />
    );
  }

  @autobind
  private _selectAllPeopleCheckbox(ev: React.FormEvent<HTMLElement>, isChecked: boolean) {
    let newPeople = this.state.people;
    newPeople.map(newPerson => {
      newPerson.checked = isChecked
      return newPerson
    });
    this.setState({ people: newPeople });
  }

  @autobind
  private _renderPeople(people: INotifyPersonRecipient[]): JSX.Element {
    const state = this.state;
    const peopleIsScrollable = state.people.length > 5 ? " scroll " : "";
    return (
      <div className={ css("od-Notify-people-container") }>
        <Label className={ css("od-Notify-peopleLabel") }>
          { stringResx.notifyPeopleLabel }
        </Label>
        { state.people.length > 0 &&
          <Checkbox
            label={ stringResx.notifySelectAllPeople }
            onChange={ this._selectAllPeopleCheckbox }
            defaultChecked={ false }
            className={ css('od-Notify-people-selectAll-checkbox') }
          />
        }
        <div
          className={ css(
            "od-Notify-people-recipient-container" + peopleIsScrollable
          ) }
        >
          { people.map(person => {
            return (
              this._renderPerson(person)
            );
          }) }
        </div>
      </div>
    );
  }

  private _renderActivityIndicator(): React.ReactElement<{}> {
    if (this.state.showActivityIndicator) {
      return (
        <ActivityIndicator message={ stringResx.activityMessageSendingMail } />
      );
    }
  }

  @autobind
  private _renderNotification(): JSX.Element {
    const props = this.props;
    const sharingInformation = props.sharingInformation;
    const currentSettings = props.currentSettings;
    const companyName = this._sharingStore.getCompanyName();

    const sharingHintProps = {
      companyName: companyName,
      currentSettings: currentSettings,
      sharingInformation: sharingInformation,
      clientId: props.clientId,
      onShareHintClick: null,
      linkRecipients: this._allRecipients(),
      customLabeledAudienceConfig: props.customLabeledAudienceConfig
    };

    return (
      <ShareNotification
        shareType={ ShareType.share }
        itemName={ sharingInformation.item.name }
        linkUrl={ null }
        clientId={ this.props.clientId }
        sharingHintProps={ sharingHintProps }
        shouldCopyLinkToClipboard={ true }
        customLabeledAudienceConfig={ this.props.customLabeledAudienceConfig }
      />
    );
  }

  @autobind
  private _displayTotalMemberCountString(): string {
    let memberCountString: string;
    let count: number = this._computeTotalMemberCount();
    if (this.state.groups.filter(g => g.checked).length > 0) {
      memberCountString = StringHelper.format(
        stringResx.notifyMembersTotalPlus,
        count
      );
    } else if (count === 1) {
      memberCountString = StringHelper.format(stringResx.notifyMemberTotal);
    } else if (count === 0) {
      memberCountString = "";
    } else {
      memberCountString = StringHelper.format(
        stringResx.notifyMembersTotal,
        count
      );
    }
    return memberCountString;
  }

  @autobind
  private _tooManyRecipientsErrorString(): string {
    return stringResx.peoplePickerLimitMessage;
  }

  @autobind
  private _displayTotalMembersCountOrError(): JSX.Element {
    return (
      <Label className={ css("od-Notify-totalMembersCount") }>
        { this._computeTotalMemberCount() >= RECIPIENT_LIMIT ? this._tooManyRecipientsErrorString() : this._displayTotalMemberCountString() }
      </Label>
    );
  }

  @autobind
  private _displayTotalGroupMemberCountString(): string {
    let memberCountString: string;
    let count: number = this._computeTotalGroupsMembersCount();

    if (count === 0) {
      memberCountString = "";
    } else {
      memberCountString = StringHelper.format(
        stringResx.notifyGroupsMembersTotalPlus,
        count
      );
    }
    return memberCountString;
  }

  @autobind
  private _displayTotalGroupsMembersCount(): JSX.Element {
    return (
      <Label className={ css("od-Notify-totalGroupsMembersCount") }>
        { this._displayTotalGroupMemberCountString() }
      </Label>
    );
  }

  @autobind
  private _renderWarning(): JSX.Element {
    const totalRecipients = this._computeTotalMemberCount();
    const warningMessage = StringHelper.format(stringResx.notifyLargeRecipientWarning, totalRecipients);

    return (totalRecipients > LARGE_RECIPIENT_WARNING && totalRecipients < RECIPIENT_LIMIT &&
      <div className="od-Notify-recipientWarning-container" >
        <MessageBar
          className={ css("od-Notify-recipientWarning") }
          messageBarType={ MessageBarType.severeWarning }
          isMultiline={ true }
        >
          { warningMessage }
        </MessageBar>
      </div>
    );
  }

  @autobind
  private _sendButtonError(): JSX.Element {
    return <Label>{ stringResx.notifySendButtonError }</Label>;
  }

  @autobind
  private _renderNotifyMain(): JSX.Element {
    const state = this.state;
    const props = this.props;
    const blockerClass: string = state.showActivityIndicator ? " blocker" : "";
    const itemName = ShareHelper.truncateItemNameForShareNotification(
      this.props.sharingInformation.item.name,
      10
    );
    return (
      <div className={ "od-Notify" + blockerClass }>
        <div className={ "od-Notify-headerAndMessage" }>
          <Header clientId={ props.clientId } viewState={ ShareViewState.notify } />
          <Label className={ css("od-Notify-message") }>
            { StringHelper.format(stringResx.notifyMessage, itemName) }
          </Label>
        </div>
        { this._renderWarning() }
        { !state.isExpanded && (
          <div>
            <div className={ css("od-Notify-horizontalLine") } />
            <Link
              onClick={ this._onExpandClick }
              className={ css("od-Notify-show-link") }
            >
              { stringResx.sendNotifyLinkExpandLink }
            </Link>
          </div>
        ) }

        { state.isExpanded && (
          <div>
            { this._renderGroups(state.groups) }
            { this._renderPeople(state.people) }
            { this._renderPeoplePicker() }
            <div className={ css("od-Notify-sendNotification-button") }>
              <SendButton
                ctaLabel={ stringResx.sendNotifyLinkButton }
                currentSettings={ props.currentSettings }
                errorMessage={
                  (this._allRecipients().length < 1 || this._computeTotalMemberCount() >= RECIPIENT_LIMIT)
                    ? this._sendButtonError()
                    : state.peoplePickerError
                }
                linkRecipients={ this._allRecipients() }
                maximumMessageLength={ 1 }
                maximumSubjectLength={ 1 }
                messageText={ "" }
                subjectText={ "" }
                onSendLinkClicked={ this._sendNotification }
                permissionsMap={ state.permissionsMap }
                sendButtonType={ SendButtonType.button }
                sharingInformation={ props.sharingInformation }
              />
            </div>
            { this._displayTotalMembersCountOrError() }
          </div>
        ) }
      </div>
    );
  }

  @autobind
  private _renderViews(): JSX.Element {
    const state = this.state;

    switch (state.viewState) {
      case ShareViewState.linkSuccess:
        return this._renderNotification();
      default:
        return this._renderNotifyMain();
    }
  }

  public render(): React.ReactElement<{}> {
    const state = this.state;
    const blockerClass: string = state.showActivityIndicator ? " blocker" : "";

    return (
      <div className={ "od-Notify" + blockerClass } >
        { this._renderViews() }
        { this._renderActivityIndicator()
        }
      </div >
    );
  }
}
